// ----------------------------------------------------------------------------------------------------
import { ReactNode, Component, PureComponent, CSSProperties, Fragment } from 'react';
import _ from 'lodash';
import moment from 'moment';
// ----------------------------------------------------------------------------------------------------
import { IWidgetThingsProps, WidgetLocalizationKeys, PartialIWidgetThingsProps, IconURIPath } from '../WidgetsThingsStructs';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
// ----------------------------------------------------------------------------------------------------
import { IWidgetClassParent } from '@UI/Widgets/IWidgetClassParent';
import { IOutline, TitleAlign, ITrackUpdates, ITsViewFormat, WidgetTitleStyle } from "@ModelViews/ModelViews";
import Factory from '@UI/Widgets/Factory';
// ----------------------------------------------------------------------------------------------------
import { DashboardManagerParent } from '../../../Dashboard/DashboardManagerParent';
import { DummyWebNode } from '../Utils/DummyWebNode';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { eIconType, SettingsType } from '@Primitive/SettingsListType';
import { DateFormat } from '@Utils/Formats';
import { WebNode } from '@API/WebNode';
import { WebModule } from '@API/WebModule';
import { IWidgetDescription, IWidgetDictionary, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import { Canvas, CanvasItemLayoutDescDictionary, CanvasLayout } from '@UI/Canvas/Canvas';
import { getRandomSmallKey, isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { isWebThingKey, StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import { PlegmaStatics } from '@API/PlegmaStatics';
import { Plegma } from '@API/Api';
import { ToolbarIconButton, ToolbarIconButtonProps } from '@Primitive/ToolbarIconButton';
import { SettingsList } from '@Primitive/SettingsList';
import { DomUtils } from '@Utils/Dom';
import { WebThing, WebThingPort } from '@API/WebThing';
// ----------------------------------------------------------------------------------------------------
const setupValidator = () => {
    // Validator for toolbar icons.
    const iconsValidatorObj = {
        type: 'array',
        items: {
            type: 'object',
            properties: {
                id: { type: 'string' },
                icon: { type: 'string' },
                iconType: { enum: [0, 1, 2] },
                iconColor: { type: 'string' },
                tooltip: { type: 'string' },
            },
            required: ['id']
        }
    };
    // Validator for setup port.
    return JsonSchemaValidator.GetInstance('dynamicPageSetupPortValidator', {
        type: 'object',
        properties: {
            width: { type: 'number' },
            height: { type: 'number' },
            widgets: {
                type: 'array',
                items: {
                    type: 'object',
                    required: ['id', 'type', 'x', 'y'],
                    properties: {
                        id: { type: ['number', 'string'] },
                        width: { type: 'number', minimum: 0 },
                        height: { type: 'number', minimum: 0 },
                        x: { type: 'number', minimum: 0 },
                        y: { type: 'number', minimum: 0 },
                        type: {
                            enum: [
                                'knob',
                                'maps',
                                'tile',
                                'infotip',
                                'stattile',
                                'markdown',
                                'barchart',
                                'piechart',
                                'linechart',
                                'gaugelabel',
                                'bubblechart',
                                'radialgauge',
                                'lineargauge',
                                'imageviewer',
                                'doughnutchart',
                                'polarareachart',
                                'buttonsingleton',
                                'percentagegauge',
                                'dynamicform',
                                'heatmap',
                                'table',
                                'led',
                                'amchart',
                                'daterange'
                            ]
                        },
                        title: { type: 'string' },
                        settings: { type: 'object' },
                        toolbarIcons: iconsValidatorObj,
                        trackUpdates: {
                            type: 'object',
                            properties: {
                                enable: { type: 'boolean' },
                                timestamp: { type: 'string' },
                                isExplicit: { type: 'boolean' },
                                dateFormat: { enum: [0, 1, 2, 3] },
                                viewFormat: { enum: [0, 1, 2] }
                            }
                        },
                        beginInLoadingState: { type: 'boolean' },
                        positioningBehavior: {
                            type: 'object',
                            properties: {
                                isStatic: { type: 'boolean' },
                                isResizable: { type: 'boolean' },
                                isDraggable: { type: 'boolean' }
                            }
                        }
                    }
                }
            },
            backgroundImage: { type: 'string' },
            toolbarIcons: iconsValidatorObj
        }
    });
}
// ----------------------------------------------------------------------------------------------------
const dataValidator = () => JsonSchemaValidator.GetInstance(
    'dynamicPageDataPortValidator',
    {
        type: 'array',
        items: {
            type: 'object',
            required: ['id'],
            properties: {
                id: { type: ['number', 'string'] },
                data: { type: ['number', 'string', 'object', 'array', 'boolean'] },
                ports: { type: 'object' },
                timestamp: { type: 'string' }
            }
        }
    }
);
// ----------------------------------------------------------------------------------------------------
interface ISetupIn {
    backgroundImage: string;
    width: number;
    height: number;
    toolbarIcons: IToolbarIcon[];
    widgets: IWidgetSetup[];
}
// ----------------------------------------------------------------------------------------------------
interface IDataIn {
    id: number | string;
    data: any;
    timestamp: string;

    /** Data per ports data */
    ports: { [key: string]: any };
}
// ----------------------------------------------------------------------------------------------------
interface IWidgetBaseSetup {
    id: number | string;
    width: number;
    height: number;
    x: number;
    y: number;
}
// ----------------------------------------------------------------------------------------------------
interface IWidgetSetup extends IWidgetBaseSetup {
    type: string;
    title: string;
    settings: any;
    widgetSettings?: {
        backgroundColor?: string;
        titleStyle?: {
            backgroundColor?: string;
            fontColor?: string;
            icon?: string;
        };
    };
    toolbarIcons: IToolbarIcon[];
    trackUpdates: _ITrackUpdates;
    beginInLoadingState: boolean;
    positioningBehavior: IPositioningBehavior;
}
// ----------------------------------------------------------------------------------------------------
interface IPositioningBehavior {
    isStatic: boolean;
    isResizable: boolean;
    isDraggable: boolean;
}
// ----------------------------------------------------------------------------------------------------
export interface _ITrackUpdates {
    enable: boolean;
    timestamp: string;
    isExplicit: boolean;
    dateFormat: DateFormat;
    viewFormat: ITsViewFormat;
}
// ----------------------------------------------------------------------------------------------------
interface IToolbarIcon {
    id: string;
    tooltip: string;
    icon: string;
    iconType: eIconType;
    iconColor: string;
}
// ----------------------------------------------------------------------------------------------------
interface IToolbarIconOut {
    icon: IToolbarIcon;
    widget: IWidgetSetup;
}
// ----------------------------------------------------------------------------------------------------
interface IWidgetDataOut {
    portKey: string;
    widget: IWidgetSetup;
    data: any;
}
// ----------------------------------------------------------------------------------------------------
interface IMargin {
    horizontal: number;
    vertical: number;
}
// ----------------------------------------------------------------------------------------------------
interface IDynamicPageSettings {
    /**
     * Allows you to move and/or resize the widgets.
     */
    editable: boolean;

    /**
     * Sets a background image for the canvas.
     */
    bgImg: string;

    /**
     * The space around canvas.
     */
    padding: number;

    /**
     * Widgets are closely and neatly packed together.
     */
    compactLayout: boolean;

    /**
     * A framework of spaced bars that are parallel.
     */
    gridLayout: boolean;

    /**
     * The number of columns displayed in the grid.
     */
    sizeW: number;

    /**
     * Fills the remaining vertical space.
     */
    fillSpace: boolean;

    /**
     * Defines the width of widget (# of grid cells).
     */
    widgetWd: number;

    /**
     * Defines the height of widget (# of grid cells).
     */
    widgetHt: number;

    /**
     * Specifies a fixed space around widget in px.
     */
    widgetMargin: IMargin;

    /**
     * Represents the depth of the widget's paper/container shadow.
     */
    zDepth: number;

    /**
     * Sets the background color of the widget.
     */
    bgColor: string;

    /**
     * Defines the radius of the widget's corners.
     */
    borderRadius: number;

    /**
     * Specifies the width and color of the 
     * outline that is drawn around widget.
     */
    outline: IOutline;

    /**
     * Adds a toolbar icon to open widget in modal dialog.
     */
    zoom: boolean;

    /**
     * Allows you to add toolbar icons to widget.
     */
    toolbarIcons: IToolbarIcon[];

    /**
     * Customizes the view of toolbar title.
     */
    titleStyle: WidgetTitleStyle;

    /**
     * Customizes the tracking of view updates.
     */
    trackUpdates: ITrackUpdates;
}
// ----------------------------------------------------------------------------------------------------
interface IDynamicPageState {
    widgetWd: number;
    widgetHt: number;
    toolbarIcons: IToolbarIcon[];
    bgImg: string;
    editMode: boolean;
}
// ----------------------------------------------------------------------------------------------------
class DynamicPage extends Component<PartialIWidgetThingsProps<IDynamicPageSettings>, IDynamicPageState> {
    // ------------------------------------------------------------------------------------------------
    private _node: WebNode;
    private _module: WebModule;
    // ------------------------------------------------------------------------------------------------
    private _widgets: IWidgetDictionary;
    private _widgetSetups: { [key: string]: IWidgetSetup; };
    // ------------------------------------------------------------------------------------------------
    private _previousTs: { [portKey: string]: number };
    // ------------------------------------------------------------------------------------------------
    private _layout: CanvasItemLayoutDescDictionary;
    // ------------------------------------------------------------------------------------------------
    private static _widgetDescs: { [key: string]: IWidgetDescription };
    private static _widgetTypes: string[];
    private static _widgetTypesMapping: { [key: string]: string };
    // ------------------------------------------------------------------------------------------------
    constructor(props: PartialIWidgetThingsProps<IDynamicPageSettings>) {
        super(props);

        if (!isDefined(DynamicPage._widgetTypes)) {

            // Set widget types.
            DynamicPage._widgetTypes = [
                'KnobWidget',
                'MapsWidget',
                'TileWidget',
                'MarkdownWidget',
                'StatTileWidget',
                'BarChartWidget',
                'PieChartWidget',
                'LineChartWidget',
                'GaugeLabelWidget',
                'BubbleChartWidget',
                'InfoTipCoreWidget',
                'RadialGaugeWidget',
                'LinearGaugeWidget',
                'ImageViewerWidget',
                'DoughnutChartWidget',
                'PolarAreaChartWidget',
                'ButtonSingletonWidget',
                'PercentageGaugeWidget',
                'DynamicFormWidget',
                'HeatmapWidget',
                'TableWidget',
                'LedWidget',
                'amChartsWidget',
                'DateRangePickerWidget'
            ];

            // Set widget types mapping.
            DynamicPage._widgetTypesMapping = {
                'knob': 'KnobWidget',
                'maps': 'MapsWidget',
                'tile': 'TileWidget',
                'markdown': 'MarkdownWidget',
                'stattile': 'StatTileWidget',
                'barchart': 'BarChartWidget',
                'piechart': 'PieChartWidget',
                'infotip': 'InfoTipCoreWidget',
                'linechart': 'LineChartWidget',
                'gaugelabel': 'GaugeLabelWidget',
                'bubblechart': 'BubbleChartWidget',
                'radialgauge': 'RadialGaugeWidget',
                'lineargauge': 'LinearGaugeWidget',
                'imageviewer': 'ImageViewerWidget',
                'doughnutchart': 'DoughnutChartWidget',
                'polarareachart': 'PolarAreaChartWidget',
                'buttonsingleton': 'ButtonSingletonWidget',
                'percentagegauge': 'PercentageGaugeWidget',
                'dynamicform': 'DynamicFormWidget',
                'heatmap': 'HeatmapWidget',
                'table': 'TableWidget',
                'led': 'LedWidget',
                'amchart': 'amChartsWidget',
                'daterange': 'DateRangePickerWidget'
            };

            // Set widget descriptors.
            DynamicPage._widgetDescs = {};
            _.forEach(
                DynamicPage._widgetTypes,
                v => DynamicPage._widgetDescs[v] =
                    Factory.CreateWidgetThingDescDefault(v)
            );
        }

        this._widgets = {};
        this._widgetSetups = {};

        // Initialize node and module.
        const node = DummyWebNode.getDummyWebNodeAndWebModule(
            this.props.ThingKey,
            this.portEventOutCb,
            'dynamic_page_node_key'
        );
        this._node = node.webNode;
        this._module = node.webModule;
        this._module.SetSessionBasePorts(false);

        const parent = DashboardManagerParent.GetInstance(this.props.NodeKey);
        const newParent = new IWidgetClassParent(
            null,
            isDefined(parent) ? () => parent.Info.inEditMode() : () => false,
            isDefined(parent) ? parent.Info.SupportEditing : false,
            isDefined(parent) ? () => parent.Info.inDebug() : () => false,
            isDefined(parent) ? parent.Info.DialogConf : null,
            { showCopy: true }
        );

        let _parent = DashboardManagerParent.GetInstance(this._node.NodeKey);
        if (!isDefined(_parent))
            _parent = new DashboardManagerParent(this._node.NodeKey, newParent);
        else
            _parent.Info = newParent;

        // Initialize state.
        this.state = this.getStateFromProps(this.props.WidgetData);
    }
    // ------------------------------------------------------------------------------------------------
    componentDidMount() {
        this._previousTs = {};
        this.updateStateFromPorts(this.props);
    }
    // ------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: IWidgetThingsProps<IDynamicPageSettings>) {
        // Settings changed?
        if (!_.isEqual(this.props.WidgetData, prevProps.WidgetData))
            this.setState(this.getStateFromProps(this.props.WidgetData));

        // Read ports and update state.
        if (this.props.PortVersion != prevProps.PortVersion)
            this.updateStateFromPorts(this.props);
    }
    // ------------------------------------------------------------------------------------------------
    getStateFromProps = (props: IDynamicPageSettings): IDynamicPageState => {
        // Construct state from props.
        return {
            widgetWd: props.widgetWd,
            widgetHt: props.widgetHt,
            toolbarIcons: props.toolbarIcons,
            bgImg: props.bgImg
        } as IDynamicPageState;
    }
    // ------------------------------------------------------------------------------------------------
    updateStateFromPorts = (props: PartialIWidgetThingsProps<IDynamicPageSettings>) => {
        const states = props.PortsStates;

        // Are there any setup?
        if (_.has(states, PortKey.setup))
            this.updateStateFromPort(
                PortKey.setup,
                states[PortKey.setup].State,
                states[PortKey.setup].LastStateStatus,
                states[PortKey.setup].Timestamp,
                props.WidgetDefaultValues[PortKey.setup],
                props.WidgetData.trackUpdates
            );
        // Are there any data?
        if (_.has(states, PortKey.data))
            this.updateStateFromPort(
                PortKey.data,
                states[PortKey.data].State,
                states[PortKey.data].LastStateStatus,
                states[PortKey.data].Timestamp,
                props.WidgetDefaultValues[PortKey.data],
                props.WidgetData.trackUpdates
            );
    }
    // ------------------------------------------------------------------------------------------------
    updateStateFromPort = (
        key: PortKey,
        state: any,
        status: StateStatus,
        ts: number,
        defaultValues: any,
        defaultTrackUpdates: ITrackUpdates
    ) => {
        // is it a new event?
        if (isDefined(ts)) {
            if (this._previousTs[key] >= ts) {
                return;
            } else {
                this._previousTs[key] = ts;
            }
        }

        switch (key) {
            case PortKey.setup:
                let setup: ISetupIn;

                // Use state depending on status.
                if (status == StateStatus.NoData) {
                    if (!isDefined(defaultValues))
                        return;
                    setup = _.cloneDeep(defaultValues);
                }
                else if (status == StateStatus.Valid)
                    setup = _.cloneDeep(state);
                else
                    return;

                // New width for widgets?
                let willUpdate = false;
                let newComponentState: Partial<IDynamicPageState> = {};
                if (_.isNumber(setup.width) &&
                    setup.width != this.state.widgetWd) {
                    willUpdate = true;
                    newComponentState.widgetWd = setup.width;
                }

                // New height for widgets?
                if (_.isNumber(setup.height) &&
                    setup.height != this.state.widgetHt) {
                    willUpdate = true;
                    newComponentState.widgetHt = setup.height;
                }

                // New background image for canvas?
                if (_.isString(setup.backgroundImage) &&
                    setup.backgroundImage != this.state.bgImg) {
                    willUpdate = true;
                    newComponentState.bgImg = setup.backgroundImage;
                }

                // New icons for widgets?
                if (_.isArray(setup.toolbarIcons)) {
                    willUpdate = true;
                    newComponentState.toolbarIcons = setup.toolbarIcons;
                }

                // Any setup?
                if (_.isArray(setup.widgets)) {

                    // Remove all things.
                    this._module
                        .RemoveThings(
                            _.keys(this._module.Things),
                            false);

                    const _trackUpdates = _.mapValues(
                        this._widgetSetups,
                        v => v.trackUpdates);

                    this._widgets = {};
                    this._widgetSetups = {};

                    // New widgets setup.
                    _.forEach(setup.widgets, v => {

                        // Valid incoming type?
                        const type = DynamicPage._widgetTypesMapping[v.type];
                        if (!isDefined(type) || !_.has(DynamicPage._widgetDescs, type))
                            return;

                        // Update title and key.
                        let desc = _.cloneDeep(DynamicPage._widgetDescs[type]);
                        desc.WidgetTitle = v.title;
                        desc.Key += '_' + this.fixWidgetId(v.id);

                        // Any new settings?
                        if (isDefined(v.settings))
                            desc.Data = v.settings;

                        if (!isDefined(v.trackUpdates))
                            v.trackUpdates = _trackUpdates[desc.Key];

                        // Any settings for track updates?
                        if (isDefined(v.trackUpdates)) {
                            v.trackUpdates.enable = v.trackUpdates.enable !== false;
                            desc.TrackUpdates = {
                                ShowLastUpdateTs: v.trackUpdates.enable,
                                DateFormat: v.trackUpdates.dateFormat,
                                ViewFormat: v.trackUpdates.viewFormat,
                                Timestamp: v.trackUpdates.timestamp,
                                IsExplicit: v.trackUpdates.isExplicit
                            };
                        }

                        // Show loading indicator and wait for data?
                        if (isDefined(v.beginInLoadingState))
                            desc.StartWithLoading = v.beginInLoadingState;

                        // Create a new widget.
                        let widget = Factory.CommonCreateWidgetThing(desc, this._module);
                        widget.Description = _.merge({}, widget.Description, widget.OverrideDescription);

                        // Pass settings to widget setting
                        if (isDefined(v.widgetSettings)) {
                            if (isDefined(v.widgetSettings.backgroundColor))
                                widget.Description.WidgetBackgroundColor = v.widgetSettings.backgroundColor;
                            if (isDefined(v.widgetSettings.titleStyle)) {
                                if (isDefined(v.widgetSettings.titleStyle.backgroundColor))
                                    widget.Description.TitleStyle.BackgroundColor = v.widgetSettings.titleStyle.backgroundColor;
                                if (isDefined(v.widgetSettings.titleStyle.fontColor))
                                    widget.Description.TitleStyle.FontColor = v.widgetSettings.titleStyle.fontColor;
                                if (isDefined(v.widgetSettings.titleStyle.icon))
                                    widget.Description.TitleStyle.Icon = v.widgetSettings.titleStyle.icon;
                            }
                        }

                        // Update private fields.
                        this._widgets[v.id] = widget;
                        this._widgetSetups[widget.Description.Key] = v;
                    });

                    // Send to react the new states
                    if (willUpdate)
                        this.setState(newComponentState as IDynamicPageState);
                    else
                        this.forceUpdate();

                    // Update the callback for outgoing port events.
                    DummyWebNode.updatePortEventsOutCb(
                        this.props.ThingKey,
                        this.portEventOutCb
                    );
                }
                break;

            case PortKey.data:
                // Valid status?
                if (status != StateStatus.Valid)
                    return;

                let needUpdate = false;
                _.forEach(state as IDataIn[], v => {
                    if (!isDefined(v.id))
                        return;

                    // Find widget from ID.
                    const widget = this._widgets[v.id];
                    if (!isDefined(widget))
                        return;

                    const setup = this._widgetSetups[widget.Description.Key];
                    if (!isDefined(setup))
                        return;

                    const timestamp = v.timestamp;
                    if (isDefined(setup.trackUpdates)) {
                        // Update timestamp of setup settings.
                        needUpdate = setup.trackUpdates.timestamp != timestamp;
                        setup.trackUpdates.timestamp = timestamp;
                    }
                    else if (defaultTrackUpdates.ShowLastUpdateTs) {
                        // Initialize setup tracking.
                        setup.trackUpdates = {
                            enable: true,
                            timestamp: timestamp,
                            isExplicit: defaultTrackUpdates.IsExplicit,
                            dateFormat: defaultTrackUpdates.DateFormat,
                            viewFormat: defaultTrackUpdates.ViewFormat
                        };
                        needUpdate = true;
                    }

                    const inst = widget as IWidgetThingsClass<any>;
                    let portEvents = [];

                    // Check the type of ports
                    if (v.ports != null) {
                        // create 
                        for (let p in v.ports)
                            if (_.has(inst.Thing?.Ports, p))
                                portEvents.push({
                                    RevNum: -1,
                                    State: v.ports[p],
                                    Timestamp: moment.utc().unix(),
                                    PortKey: `${inst.ThingKey}${PlegmaStatics.PlegmaAPI.KeySeparator}${p}`
                                });
                    } else {
                        // Get target port key.
                        const portKey = inst.GetDataPortKeyStatic();
                        if (IsNullOrEmpty(portKey))
                            return;

                        portEvents = [{
                            RevNum: -1,
                            State: v.data,
                            Timestamp: moment.utc().unix(),
                            PortKey: `${inst.ThingKey}${PlegmaStatics.PlegmaAPI.KeySeparator}${portKey}`
                        }];
                    }

                    // Handle it like an incoming event.
                    this._node.YodiWS.onPortEvents(portEvents);
                });

                // re-render to show timestamps
                if (needUpdate) {
                    this.forceUpdate();
                }
                break;

            default:
                break;
        }
    }
    // ------------------------------------------------------------------------------------------------
    fixWidgetId = (id: number | string): string => String(id).replace('_', '1').replace('-', '1')
    // ------------------------------------------------------------------------------------------------
    portEventOutCb = (events: Plegma.PortEvent[]) => {
        if (_.isEmpty(this._widgetSetups))
            return;

        _.forEach(events, v => {
            // Extract port key.
            const portKeyIdx = v.PortKey.lastIndexOf('-');
            const portKey = v.PortKey.substring(portKeyIdx + 1);
            if (isWebThingKey(portKey))
                return;

            // Extract widget key.
            let widgetKey = v.PortKey.substring(0, portKeyIdx);
            widgetKey = widgetKey.replace(this.props.ThingKey + '_', '');

            let state = v.State;
            if (_.isString(state)) {
                state = _.trim(state);

                // Parse state if looks like JSON.
                if ((_.startsWith(state, '{') && _.endsWith(state, '}')) ||
                    (_.startsWith(state, '[') && _.endsWith(state, ']')))
                    try { state = JSON.parse(state); }
                    catch (e) { }
            }
            // Send port event with related info.
            this.props.SendPortEvent(PortKey.widgetData, JSON.stringify({
                portKey: portKey,
                data: state,
                widget: this._widgetSetups[widgetKey]
            } as IWidgetDataOut));
        });
    }
    // ------------------------------------------------------------------------------------------------
    exitEditMode = () => {
        // Discard layout changes and exit from edit mode.
        this._layout = null;
        this.setState({ editMode: false });
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        // Props destructuring.
        const {
            width,
            height,
            WidgetData,
            SendPortEvent,
            LocalizationMap
        } = this.props;

        // Settings destructuring.
        const {
            editable,
            padding,
            compactLayout,
            gridLayout,
            sizeW,
            zoom,
            zDepth,
            outline,
            bgColor,
            fillSpace,
            titleStyle,
            borderRadius,
            widgetMargin,
            trackUpdates,
        } = WidgetData;

        // State destructuring.
        const {
            widgetWd,
            widgetHt,
            toolbarIcons,
            bgImg,
            editMode
        } = this.state;

        const toolbarHt = 25;
        const canvasHt = editable ? height - toolbarHt : height;

        /*let yMax: number;
        let htPixels: number;

        if (fillSpace) {
            // Compute the pixels per vertical unit.
            htPixels = Canvas.GetWidthPixels(
                width,
                sizeW,
                padding
            ) * Canvas.InvAspectRatio;

            // Find the lowest position.
            yMax = Math.floor(canvasHt / htPixels);
            _.forEach(this._widgetSetups, v => {
                let bottom = v.y + v.height;
                if (yMax < bottom)
                    yMax = bottom;
            });
        }*/

        // Initialize layout.
        let layout: CanvasLayout = {
            ItemDesc: {},
            LayoutDesc: {}
        };

        _.forEach(this._widgets, v => {

            let desc = v.Description;
            const key = desc.Key;

            const setup = this._widgetSetups[key];
            if (!isDefined(setup))
                return;

            // Set the tracking of view updates.
            const _trackUpdates = setup.trackUpdates;
            desc.TrackUpdates = isDefined(_trackUpdates)
                ? {
                    ShowLastUpdateTs: _trackUpdates.enable,
                    DateFormat: _trackUpdates.dateFormat,
                    ViewFormat: _trackUpdates.viewFormat,
                    Timestamp: _trackUpdates.timestamp,
                    IsExplicit: _trackUpdates.isExplicit
                } : trackUpdates;

            // Create item descriptor.
            layout.ItemDesc[key] = {
                Key: key,

                ShowExtraMenu: zoom,
                Title: desc.WidgetTitle,
                ToolbarIcon: desc.TitleStyle?.Icon ?? titleStyle.Icon,
                Alignment: titleStyle.Alignment,
                TitleHeight: titleStyle.TitleHeight,
                ToolbarFontColor: desc.TitleStyle?.FontColor ?? titleStyle.FontColor,
                ToolbarBackgroundColor: desc.TitleStyle?.BackgroundColor ?? titleStyle.BackgroundColor,
                ToolbarFontAutoSize: false,

                zDepth: zDepth,
                IsHidden: false,
                Outline: outline,
                BackgroundColor: desc.WidgetBackgroundColor ?? bgColor,

                Render: (k, w, h) => v.RenderBase(k, w, h),
                RenderToolbar: (k, h) => {

                    // Get icons for widget.
                    let icons = setup.toolbarIcons;
                    if (!_.isArray(icons))
                        icons = toolbarIcons;

                    let toolbarIconsNodes: ReactNode[] = _.map(icons, i => {

                        // Initial props for icon.
                        let props: ToolbarIconButtonProps = {
                            iconType: i.iconType,
                            style: {
                                marginLeft: 10,
                                cursor: 'pointer',
                                color: i.iconColor
                            }
                        };

                        // Depending on type, set more props.
                        if (i.iconType == eIconType.fontAwesome)
                            props.className = i.icon;
                        else {
                            props.src = i.icon;
                            props.height = Math.ceil(titleStyle.TitleHeight * 0.8);
                            props.style.paddingBottom = Math.ceil(titleStyle.TitleHeight * 0.05);
                        }

                        // Create icon element.
                        return <ToolbarIconButton
                            {...props}
                            id='icons'
                            key={'icon-' + i.id}
                            tooltip={i.tooltip}
                            onClick={() => {
                                SendPortEvent(PortKey.toolbarIcon, JSON.stringify({
                                    icon: i,
                                    widget: setup
                                } as IToolbarIconOut));
                            }} />
                    });

                    let baseToolbarIconsNodes = v.RenderBaseToolbar(k, h);
                    return _.concat(toolbarIconsNodes, baseToolbarIconsNodes);
                },
                ToolbarDynamicTooltip: () => v.ToolbarDynamicTooltip()
            };

            let x = setup.x;
            let y = setup.y;

            let w = _.isNumber(setup.width) ? setup.width : widgetWd;
            let h = _.isNumber(setup.height) ? setup.height : widgetHt;

            // if in edit mode then use the new layout
            if (isDefined(this._layout)) {
                let itemLayout = this._layout[key];

                if (isDefined(itemLayout)) {
                    x = itemLayout.X;
                    y = itemLayout.Y;

                    w = itemLayout.W;
                    h = itemLayout.H;
                }
            }

            if (w > sizeW) w = sizeW;
            if (x >= sizeW) x = sizeW - w;

            if (x + w > sizeW) w = sizeW - x;
            //if (fillSpace) h = yMax - y;

            // Create layout descriptor.
            layout.LayoutDesc[key] = {
                Key: key,

                X: x,
                Y: y,

                W: w,
                H: h,

                MinW: 1,
                MinH: 1,

                MaxH: undefined,
                MaxW: undefined,

                IsStatic: setup.positioningBehavior?.isStatic ?? false,
                IsDraggable: setup.positioningBehavior?.isDraggable ?? true,
                IsResizable: setup.positioningBehavior?.isResizable ?? true,

                DefiesGrid: desc.DefiesGrid,
                MobileOrder: desc.MobileOrder,
                WidgetNoBackground: desc.WidgetNoBackground
            };

            // Check and fix any collisions.
            layout.LayoutDesc = Canvas.LayoutAutoReorder(
                _.values(layout.LayoutDesc),
                layout.LayoutDesc[key],
                compactLayout
            );
        });

        const marginOffset = outline.Thickness;
        const iconStyle: CSSProperties = {
            margin: '0 6px',
            fontSize: 16,
            color: '#000000',
            cursor: 'pointer',
            lineHeight: toolbarHt + 'px'
        };

        return <Fragment>
            {editable
                ? <div style={{
                    width: width,
                    height: toolbarHt,
                    padding: '0 5px 0 10px',
                    textAlign: 'right',
                    display: 'flex',
                    justifyContent: 'flex-end'
                }}>
                    {/* Toolbar above canvas. */}
                    {editMode
                        ? <Fragment>
                            <ToolbarIconButton
                                id='icons'
                                className='fas fa-save'
                                tooltip={LocalizationMap[LocalizationKey.save]}
                                style={iconStyle}
                                onClick={() => {
                                    let baseSetups: IWidgetBaseSetup[] = [];

                                    _.forEach(this._layout, v => {
                                        // Find widget's setup.
                                        let setup = this._widgetSetups[v.Key];
                                        if (!isDefined(setup))
                                            return;

                                        // Update position and size.
                                        setup.x = v.X;
                                        setup.y = v.Y;
                                        setup.width = v.W;
                                        setup.height = v.H;

                                        // Add base setup to send.
                                        baseSetups.push({
                                            id: setup.id,
                                            width: setup.width,
                                            height: setup.height,
                                            x: setup.x,
                                            y: setup.y
                                        });
                                    });
                                    // Send the new layout setup.
                                    SendPortEvent(
                                        PortKey.layoutSetup,
                                        JSON.stringify(baseSetups)
                                    );
                                    this.exitEditMode();
                                }} />
                            <ToolbarIconButton
                                id='icons'
                                className='fas fa-times'
                                tooltip={LocalizationMap[LocalizationKey.discard]}
                                style={iconStyle}
                                onClick={() => {
                                    SendPortEvent(PortKey.layoutDiscard, true);
                                    this.exitEditMode();
                                }} />
                        </Fragment>
                        : <ToolbarIconButton
                            id='icons'
                            className='fas fa-edit'
                            tooltip={LocalizationMap[LocalizationKey.edit]}
                            style={iconStyle}
                            onClick={() => {
                                SendPortEvent(PortKey.layoutEdit, true);
                                this.setState({ editMode: true });
                            }} />}
                </div>
                : null}
            {/* Create canvas element. */}
            <Canvas
                width={width}
                height={editMode ? canvasHt / 2 : canvasHt}

                Layout={layout}
                GridLayout={gridLayout}
                CompactLayout={compactLayout}

                rootStyle={{ height: canvasHt }}
                Padding={padding}
                Margin={{
                    W: widgetMargin.horizontal + marginOffset,
                    H: widgetMargin.vertical + marginOffset
                }}

                ElementSettings={{
                    zDepth: zDepth,
                    Outline: outline,
                    NoBackground: false,
                    BackgroundColor: bgColor,
                    BorderRadius: borderRadius
                }}

                SizeW={sizeW}
                WidthListThreshold={0}
                BackgroundImage={bgImg}
                CanvasBackgroundColor='transparent'

                InEditMode={editMode}
                onCanvasLayoutChanged={layout => this._layout = layout.LayoutDesc} />
        </Fragment>;
    }
    // ------------------------------------------------------------------------------------------------
}
// ----------------------------------------------------------------------------------------------------
class DynamicPageSettings extends PureComponent<IWidgetSettingsProps<IDynamicPageSettings>> {
    // ------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<IDynamicPageSettings>) { super(props); }
    // ------------------------------------------------------------------------------------------------
    render() {
        // Render list with settings.
        const settings = this.props.WidgetData;
        return <SettingsList
            AutoSave
            Toolbar={this.props.SettingListToolbar}
            SettingsMap={[
                {
                    name: '',
                    elements: [
                        // Canvas group.
                        {
                            type: SettingsType.Group,
                            name: 'Canvas',
                            elements: [
                                {
                                    type: SettingsType.Boolean,
                                    label: 'Editable',
                                    dataPath: 'editable',
                                    tooltip: 'Allows you to move and/or resize the widgets'
                                },
                                {
                                    type: SettingsType.ImageFileInput,
                                    label: 'Background Image',
                                    dataPath: 'bgImg',
                                    tooltip: 'Sets a background image for the canvas',
                                    externalURL: true,
                                    previewImg: true,
                                    fileInterceptor: value =>
                                        new Promise<string>((resolve, reject) =>
                                            DomUtils
                                                .getResizedImage(value, 1920, 1080, 'jpeg')
                                                .then(res => resolve(res.src))
                                                .catch(e => reject(e)))
                                },
                                {
                                    type: SettingsType.Slider,
                                    label: 'Padding',
                                    dataPath: 'padding',
                                    tooltip: 'The space around canvas',
                                    minValue: 0,
                                    maxValue: 20,
                                    stepValue: 1,
                                    valuePosfix: 'px'
                                },
                                {
                                    type: SettingsType.Boolean,
                                    label: 'Compact Layout',
                                    dataPath: 'compactLayout',
                                    tooltip: 'Widgets are closely and neatly packed together'
                                },
                                {
                                    type: SettingsType.Boolean,
                                    label: 'Grid Layout',
                                    dataPath: 'gridLayout',
                                    tooltip: 'A framework of spaced bars that are parallel'
                                },
                                {
                                    type: SettingsType.Slider,
                                    label: 'Vertical Grid Size',
                                    dataPath: 'sizeW',
                                    tooltip: 'The number of columns displayed in the grid',
                                    minValue: 6,
                                    maxValue: 36,
                                    stepValue: 6,
                                    valuePosfix: ''
                                },
                                /*{
                                    type: SettingsType.Boolean,
                                    label: 'Fill Space',
                                    dataPath: 'fillSpace',
                                    tooltip: 'Fills the remaining vertical space'
                                }*/
                            ]
                        },
                        // Widget group.
                        {
                            type: SettingsType.Group,
                            name: 'Widget',
                            elements: [
                                {
                                    type: SettingsType.Slider,
                                    label: 'Width',
                                    dataPath: 'widgetWd',
                                    tooltip: 'Defines the width of widget (# of grid cells)',
                                    minValue: 1,
                                    maxValue: settings.sizeW,
                                    stepValue: 1,
                                    valuePosfix: ''
                                },
                                {
                                    type: SettingsType.Slider,
                                    label: 'Height',
                                    dataPath: 'widgetHt',
                                    tooltip: 'Defines the height of widget (# of grid cells)',
                                    minValue: 1,
                                    maxValue: settings.sizeW,
                                    stepValue: 1,
                                    valuePosfix: ''
                                },
                                {
                                    type: SettingsType.Slider,
                                    label: 'Horizontal Margin',
                                    dataPath: 'widgetMargin.horizontal',
                                    tooltip: 'Specifies a fixed left and right margin of widget in px',
                                    minValue: 0,
                                    maxValue: 10,
                                    stepValue: 1,
                                    valuePosfix: 'px'
                                },
                                {
                                    type: SettingsType.Slider,
                                    label: 'Vertical Margin',
                                    dataPath: 'widgetMargin.vertical',
                                    tooltip: 'Specifies a fixed top and bottom margin of widget in px',
                                    minValue: 0,
                                    maxValue: 10,
                                    stepValue: 1,
                                    valuePosfix: 'px'
                                },
                                {
                                    type: SettingsType.ColorPicker,
                                    label: 'Background Color',
                                    dataPath: 'bgColor',
                                    tooltip: 'Sets the background color of the widget'
                                },
                                {
                                    type: SettingsType.Slider,
                                    label: 'zDepth',
                                    dataPath: 'zDepth',
                                    tooltip: 'Represents the depth of the widget\'s paper/container shadow',
                                    minValue: 0,
                                    maxValue: 5,
                                    stepValue: 1,
                                    valuePosfix: ''
                                },
                                {
                                    type: SettingsType.Slider,
                                    label: 'Border Radius',
                                    dataPath: 'borderRadius',
                                    tooltip: 'Defines the radius of the widget\'s corners',
                                    minValue: 0,
                                    maxValue: 20,
                                    stepValue: 1,
                                    valuePosfix: 'px'
                                },
                                // Outline group.
                                {
                                    type: SettingsType.Group,
                                    name: 'Outline',
                                    elements: [
                                        {
                                            type: SettingsType.Slider,
                                            label: 'Thickness',
                                            dataPath: 'outline.Thickness',
                                            tooltip: 'Specifies the width of the outline that is drawn around widget',
                                            minValue: 0,
                                            maxValue: 8,
                                            stepValue: 1,
                                            valuePosfix: 'px'
                                        },
                                        {
                                            type: SettingsType.ColorPicker,
                                            label: 'Color',
                                            dataPath: 'outline.Color',
                                            tooltip: 'Specifies the color of the outline that is drawn around widget'
                                        }
                                    ]
                                },
                                // Toolbar group.
                                {
                                    type: SettingsType.Group,
                                    name: 'Toolbar',
                                    elements: [
                                        {
                                            type: SettingsType.Slider,
                                            label: 'Height',
                                            dataPath: 'titleStyle.TitleHeight',
                                            tooltip: 'Defines the height of toolbar in px',
                                            minValue: 25,
                                            maxValue: 40,
                                            stepValue: 1,
                                            valuePosfix: 'px'
                                        },
                                        {
                                            type: SettingsType.Boolean,
                                            label: 'Zoom',
                                            dataPath: 'zoom',
                                            tooltip: 'Adds a toolbar icon to open widget in modal dialog'
                                        },
                                        {
                                            type: SettingsType.ColorPicker,
                                            label: 'Background Color',
                                            dataPath: 'titleStyle.BackgroundColor',
                                            tooltip: 'Sets the background color of the toolbar'
                                        },
                                        {
                                            type: SettingsType.ItemsMgmtDialog,
                                            label: 'Icons',
                                            dataPath: 'toolbarIcons',
                                            tooltip: 'Allows you to add toolbar icons to widget',
                                            itemsInfo: [
                                                {
                                                    type: SettingsType.String,
                                                    label: 'ID',
                                                    dataPath: 'id',
                                                    tooltip: 'A unique identifier which is used to specify the icon',
                                                    isUnique: true
                                                },
                                                {
                                                    type: SettingsType.String,
                                                    label: 'Tooltip',
                                                    dataPath: 'tooltip',
                                                    tooltip: 'Text that would display on hover',
                                                },
                                                {
                                                    type: SettingsType.ExtendedIcon,
                                                    label: 'Icon',
                                                    dataPath: 'icon',
                                                    tooltip: 'The icon to insert into toolbar',
                                                    iconTypePath: 'iconType',
                                                    supportUpload: {
                                                        maxHeight: 64,
                                                        maxWidth: 64
                                                    },
                                                    supportUrl: true
                                                },
                                                {
                                                    type: SettingsType.ColorPicker,
                                                    label: 'Color',
                                                    dataPath: 'iconColor',
                                                    tooltip: 'Specifies the color of icon',
                                                    isEnabled: (data, row) =>
                                                        row.iconType == eIconType.fontAwesome
                                                }
                                            ],
                                            createNewItem: () => {
                                                return {
                                                    icon: '',
                                                    tooltip: '',
                                                    iconType: eIconType.fontAwesome,
                                                    id: 'icon_' + getRandomSmallKey()
                                                } as IToolbarIcon;
                                            }
                                        },
                                        // Title group.
                                        {
                                            type: SettingsType.Group,
                                            name: 'Title',
                                            tooltip: 'Customizes the view of toolbar title',
                                            elements: [
                                                {
                                                    type: SettingsType.Icon,
                                                    label: 'Icon',
                                                    dataPath: 'titleStyle.Icon',
                                                    tooltip: 'Adds an icon which can\'t be activated(selected, clicked on, etc.) to toolbar'
                                                },
                                                {
                                                    type: SettingsType.DropDown,
                                                    label: 'Alignment',
                                                    dataPath: 'titleStyle.Alignment',
                                                    tooltip: 'Specifies the horizontal alignment of text in toolbar',
                                                    items: [
                                                        { value: TitleAlign.Left, label: 'Left' },
                                                        { value: TitleAlign.Center, label: 'Center' },
                                                        { value: TitleAlign.Right, label: 'Right' }
                                                    ]
                                                },
                                                {
                                                    type: SettingsType.ColorPicker,
                                                    label: 'Color',
                                                    dataPath: 'titleStyle.FontColor',
                                                    tooltip: 'Specifies the color of text in toolbar'
                                                }
                                            ]
                                        },
                                        {
                                            type: SettingsType.Group,
                                            name: 'Track Updates',
                                            tooltip: 'Customizes the tracking of view updates',
                                            elements: [
                                                {
                                                    type: SettingsType.Boolean,
                                                    label: 'Enable',
                                                    dataPath: 'trackUpdates.ShowLastUpdateTs',
                                                    tooltip: 'Keeps track of view updates'
                                                },
                                                {
                                                    type: SettingsType.Boolean,
                                                    label: 'Explicit',
                                                    dataPath: 'trackUpdates.IsExplicit',
                                                    tooltip: 'When enabled, the timestamp needs to be provided by the data (input) port. Otherwise, the last view update (of widget) will be used.',
                                                    isHidden: data => !data.trackUpdates.ShowLastUpdateTs
                                                },
                                                {
                                                    type: SettingsType.DropDown,
                                                    label: 'Date Format',
                                                    dataPath: 'trackUpdates.DateFormat',
                                                    tooltip: 'Specifies how the date is represented',
                                                    items: [
                                                        { value: DateFormat.Short, label: 'Short' },
                                                        { value: DateFormat.Long, label: 'Long' },
                                                        { value: DateFormat.ISO, label: 'ISO' },
                                                        { value: DateFormat.ISOMS, label: 'ISO ms' }
                                                    ],
                                                    isHidden: data => !data.trackUpdates.ShowLastUpdateTs
                                                },
                                                {
                                                    type: SettingsType.DropDown,
                                                    label: 'View Format',
                                                    dataPath: 'trackUpdates.ViewFormat',
                                                    tooltip: 'Specifies how the date is displayed',
                                                    items: [
                                                        { value: ITsViewFormat.Tooltip, label: 'Icon tooltip' },
                                                        { value: ITsViewFormat.Hover, label: 'Hover tooltip' },
                                                        { value: ITsViewFormat.Text, label: 'Text' }
                                                    ],
                                                    isHidden: data => !data.trackUpdates.ShowLastUpdateTs
                                                }
                                            ]
                                        }
                                    ]
                                }
                            ]
                        }
                    ]
                }
            ]}
            Data={settings}
            onSaveData={data => {
                const onSaveCb = this.props.onWidgetDataUpdate;
                if (_.isFunction(onSaveCb))
                    onSaveCb(data);
            }} />;
    }
    // ------------------------------------------------------------------------------------------------
}
// ----------------------------------------------------------------------------------------------------
const enum LocalizationKey {
    edit = 'edit',
    save = 'save',
    discard = 'discard'
}
// ----------------------------------------------------------------------------------------------------
const defaultLocalizationValues = {
    [LocalizationKey.edit]: 'Move and/or resize widgets',
    [LocalizationKey.save]: 'Save layout changes',
    [LocalizationKey.discard]: 'Revert layout changes'
}
// ----------------------------------------------------------------------------------------------------
const enum PortKey {
    setup = 'setup',
    data = 'data',
    toolbarIcon = 'toolbar_icon',
    widgetData = 'widget_data',
    layoutSetup = 'layout_setup',
    layoutEdit = 'layout_edit',
    layoutDiscard = 'layout_discard'
}
// ----------------------------------------------------------------------------------------------------
export class DynamicPageWidget extends IWidgetThingsClass<IDynamicPageSettings> {
    // ------------------------------------------------------------------------------------------------
    Render(): ReactNode { return <DynamicPage />; }
    // ------------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): ReactNode {
        const parent = this.Parent;
        return <DynamicPageSettings
            key={'settings-' + key}
            width={width}
            height={height}
            NodeKey={this.NodeKey}
            ThingKey={this.ThingKey}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={data => this.WidgetDataUpdate(data)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null} />;
    }
    // ------------------------------------------------------------------------------------------------
    get ports(): WebThingPort[] {
        return [
            // Setup input port.
            new WebThingPort(
                {
                    Name: 'Setup',
                    PortKey: PortKey.setup,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    State: ''
                },
                WebThingPortType.Data,
                data => this.ValidateValues(PortKey.setup, data)
            ),
            // Data input port.
            new WebThingPort(
                {
                    Name: 'Data',
                    PortKey: PortKey.data,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    State: ''
                },
                WebThingPortType.Data,
                data => this.ValidateValues(PortKey.data, data)
            ),
            // Toolbar icon output port.
            new WebThingPort({
                Name: 'Toolbar Icon',
                PortKey: PortKey.toolbarIcon,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Trigger),
            // Widget data output port.
            new WebThingPort({
                Name: 'Widget Data',
                PortKey: PortKey.widgetData,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Trigger),
            // Layout setup output port.
            new WebThingPort({
                Name: 'Layout Setup',
                PortKey: PortKey.layoutSetup,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Trigger),
            new WebThingPort({
                Name: 'Layout Edit',
                PortKey: PortKey.layoutEdit,
                Type: Plegma.ePortType.Boolean,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Trigger),
            new WebThingPort({
                Name: 'Layout Discard',
                PortKey: PortKey.layoutDiscard,
                Type: Plegma.ePortType.Boolean,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Trigger)
        ] as WebThingPort[];
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        // Thing initialization.
        const webThing = new WebThing(
            this.WebModule,
            WidgetDescriptionToPlegmaThing(this.Description),
            this.ports);
        this.ThingKey = webThing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        // Get node and thing instances.
        const node = WebNode.GetInstanceKey(this.NodeKey);
        const thing = node.GetThing(this.ThingKey);

        // Ports setup.
        thing.Ports = {};
        _.forEach(this.ports, v => thing.AddPort(v));

        // Update thing.
        node.GetModule(this.WebModuleKey)
            .UpdateThing(thing);
    }
    // ------------------------------------------------------------------------------------------------
    GetDefaultWidgetData(): IDynamicPageSettings { return DynamicPageWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): IDynamicPageSettings {
        return {
            editable: false,
            bgImg: null,
            padding: 0,
            compactLayout: false,
            gridLayout: false,
            sizeW: 12,
            fillSpace: false,
            widgetWd: 4,
            widgetHt: 4,
            widgetMargin: {
                horizontal: 4,
                vertical: 4
            },
            toolbarIcons: [],
            zoom: false,
            zDepth: 1,
            bgColor: '#ffffff',
            outline: {
                Thickness: 0,
                Color: '#64B5F6'
            },
            borderRadius: 2,
            titleStyle: {
                Alignment: TitleAlign.Left,
                BackgroundColor: '#ffffff',
                FontColor: '#000000',
                Icon: '',
                TitleHeight: 25,
                FontSize: 14,
                FontAutoSize: true
            },
            trackUpdates: {
                ShowLastUpdateTs: false,
                IsExplicit: false,
                DateFormat: DateFormat.Short,
                ViewFormat: ITsViewFormat.Tooltip
            }
        };
    }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        // Widget's descriptor.
        return {
            MinW: 1,
            MinH: 1,
            Key: 'DynamicPageWidget',
            Type: 'DynamicPageWidget',
            Title: 'Dynamic Page',
            IconURI: IconURIPath + '/png/dynamic_page.png',
            Data: DynamicPageWidget.GetDefaultWidgetDataStatic()
        };
    }
    // ------------------------------------------------------------------------------------------------
    ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        // Validation for incoming event.
        switch (key) {
            case PortKey.setup:
                return setupValidator().DataValidateString(value);
            case PortKey.data:
                return dataValidator().DataValidateString(value);
            default:
                return { valid: true, errors: '' };
        }
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        let dict = {};

        // Expose widget's title (if defined).
        const widgetTitle = this.Description.WidgetTitle;
        if (!IsNullOrEmpty(widgetTitle))
            dict[WidgetLocalizationKeys.Title] = widgetTitle;

        // Expose tooltips for toolbar buttons.
        dict[LocalizationKey.edit] = _.clone(defaultLocalizationValues[LocalizationKey.edit]);
        dict[LocalizationKey.save] = _.clone(defaultLocalizationValues[LocalizationKey.save]);
        dict[LocalizationKey.discard] = _.clone(defaultLocalizationValues[LocalizationKey.discard]);

        return dict;
    }
    // ------------------------------------------------------------------------------------------------
}
// ----------------------------------------------------------------------------------------------------